import {MSA_SUPPORT} from 'model-hub/scientific-models/ScientificModelConstants';
import type {ScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {JSONObject, JSONValue} from 'util/Types';

import {
  getModelInputFields,
  SEQUENCE_COLUMN_KEY,
  SEQUENCE_COLUMNS_KEY,
  USE_MSA_KEY,
} from './runScientificModelInputFields';

function clampArrayToFieldMaxItems(
  value: JSONValue | undefined,
  spec: PipelineConfigJsonSpec
): JSONValue | undefined {
  if (
    Array.isArray(value) &&
    spec.kind === 'array' &&
    spec.maxItems != null &&
    value.length > spec.maxItems
  ) {
    return value.slice(0, spec.maxItems);
  }
  return value;
}

function fieldIsEmpty(value: JSONValue | undefined): boolean {
  return value == null || (Array.isArray(value) && value.length === 0);
}

/**
 * The previous config's value for a field. Reads from both sequenceColumn and sequenceColumns keys
 * for monomer/multimer inputs.
 */
const SEQUENCE_COLUMN_KEYS = [SEQUENCE_COLUMN_KEY, SEQUENCE_COLUMNS_KEY];

function getPreviousFieldValue(oldConfig: JSONObject | null, fieldKey: string): JSONValue | undefined {
  if (oldConfig == null) {
    return undefined;
  }
  if (SEQUENCE_COLUMN_KEYS.includes(fieldKey)) {
    const oldMonomerColumn = oldConfig[SEQUENCE_COLUMN_KEY];
    const oldMultimerColumns = oldConfig[SEQUENCE_COLUMNS_KEY];
    if (fieldIsEmpty(oldMonomerColumn) && fieldIsEmpty(oldMultimerColumns)) {
      return undefined;
    }
    const oldColumns = Array.isArray(oldMultimerColumns) ? oldMultimerColumns : [oldMonomerColumn];
    return fieldKey === SEQUENCE_COLUMN_KEY ? oldColumns[0] : oldColumns;
  }
  return oldConfig[fieldKey];
}

/**
 * Builds `modelConfig` when the user picks a different model: reuse compatible fields from the
 * previous config, apply MSA defaults, and drop keys that belong to other models.
 */
export function runScientificModelConfigOnChange({
  newValue,
  oldValue,
  registry,
}: {
  newValue: JSONValue;
  oldValue: JSONValue;
  registry: ScientificModelRegistry;
}): JSONValue {
  const newConfig = newValue as JSONObject;
  const newModelNode =
    typeof newConfig.model === 'string'
      ? registry.catalogByExternalReference.get(newConfig.model)
      : undefined;
  if (newModelNode == null) {
    return newValue;
  }

  const oldConfig = oldValue as JSONObject | null;
  // Same model: a field edit (including clearing a field), which must pass through untouched.
  if (oldConfig?.model === newModelNode.externalReference) {
    return newValue;
  }

  const fields = getModelInputFields(newModelNode) ?? [];
  const {msaSupport} = newModelNode.capabilities;

  // New merged model config: starts with the new model's fields
  const mergedModelConfig: JSONObject = {model: newModelNode.externalReference};
  for (const field of fields) {
    let fieldValue = newConfig[field.key];
    const previousValue = getPreviousFieldValue(oldConfig, field.key);

    // Reuse prev values when the new model template leaves the field empty.
    if (fieldIsEmpty(fieldValue)) {
      fieldValue = previousValue ?? null;
    }

    // MSA default: required always on; optional on when previous value is unset
    if (
      field.key === USE_MSA_KEY &&
      (msaSupport === MSA_SUPPORT.REQUIRED || (msaSupport === MSA_SUPPORT.OPTIONAL && fieldValue == null))
    ) {
      fieldValue = true;
    }
    mergedModelConfig[field.key] = clampArrayToFieldMaxItems(fieldValue, field.spec) ?? null;
  }
  return mergedModelConfig;
}
